///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ActivatedRouteSnapshot, createUrlTreeFromSnapshot, Params, ResolveFn, Routes, UrlTree } from '@angular/router';
import { UserAuthorities } from '../services/user/user-authorities';
import { GtthProjectsComponent } from './projects.component';
import { GtthFolderPopupComponent } from './dialogs/folder-dialog/folder-dialog.component';
import { GtthProjectsEngComponent } from './projects-eng.component';
import { ProjectGtthClonePopupComponent } from './dialogs/project-clone/project-gtth-clone-dialog.component';
import { ProjectGtthMovePopupComponent } from './dialogs/project-move/project-move-dialog.component';
import { GtthLayersSettingsPopupComponent } from './dialogs/layers-settings-dialog/layers-settings-dialog.component';
import { GtthCreateBOMConfiguratorPopupComponent } from './dialogs/bom-dialog/bom-config-dialog.component';
import { ProjectPopupComponent } from './dialogs/project-dialog/project-dialog.component';
import { canActivateUser } from '../shared/auth/user-route-access-service';
import { inject } from '@angular/core';
import { ProjectRoutingStateService } from './project-routing-state.service';
import { GtthLocalStorageService } from '../shared/auth/localStorage.service';
import { FoldersService, RootFoldersInfo } from '../services/project/folders.service';
import { iif, Observable, of, switchMap } from 'rxjs';
import { GtthProjectsDeletePopupComponent } from './dialogs/delete-project-dialog/delete-project-dialog.component';
import { GtthProjectsFolderDeletePopupComponent } from './dialogs/delete-folder-dialog/delete-folder-dialog.component';
import { ProjectImportPopupComponent } from './dialogs/project-files-import/project-files-import-dialog.component';

export const PROJECTS_BASE_URL = 'projects';
export const PROJECTS_ENG_BASE_URL = 'project-eng';

const pageTitle = 'project.home.title';

export const rootFolderIdsResolver: ResolveFn<Observable<RootFoldersInfo> | RootFoldersInfo> = (
    route: ActivatedRouteSnapshot
): Observable<RootFoldersInfo> | RootFoldersInfo => {
    const { customerId } = route.params;
    const projectFoldersService: FoldersService = inject(FoldersService);
    return iif(
        () => projectFoldersService.hasRootFolderIds(customerId),
        of(projectFoldersService.getRootFolderIds(customerId)),
        projectFoldersService.getFolders(customerId).pipe(switchMap(() => of(projectFoldersService.getRootFolderIds(customerId))))
    );
};

export const projectEngViewCanActivate = (route: ActivatedRouteSnapshot): boolean | UrlTree => {
    const projectStateService: ProjectRoutingStateService = inject(ProjectRoutingStateService);
    const localStorageService: GtthLocalStorageService = inject(GtthLocalStorageService);
    const project = projectStateService.getLastAndForget();

    if (!project?.customerId || project.customerId < 0 || !project.parentId) {
        return true;
    }

    // need for correct customer-accordion working after redirect
    localStorageService.setSelectedCustomerId(project.customerId);

    return createUrlTreeFromSnapshot(route, [`/${PROJECTS_ENG_BASE_URL}`], {
        page: 1,
        company_id: project.customerId,
        filter: '',
        tree_id: project.parentId || -1
    } as Params);
};

export const projectsViewCanActivate = (route: ActivatedRouteSnapshot): boolean | UrlTree => {
    const projectStateService: ProjectRoutingStateService = inject(ProjectRoutingStateService);
    const project = projectStateService.getLastAndForget();

    if (!project?.parentId) {
        return true;
    }

    return createUrlTreeFromSnapshot(route, [`/${PROJECTS_BASE_URL}`], {
        page: 1,
        tree_id: project.parentId || -1
    } as Params);
};

export const projectRoute: Routes = [
    {
        path: PROJECTS_BASE_URL,
        component: GtthProjectsComponent,
        data: {
            baseUrl: PROJECTS_BASE_URL,
            authorities: [UserAuthorities.COMPANY_ADMIN, UserAuthorities.COMPANY_PLANNER, UserAuthorities.COMPANY_USER],
            pageTitle
        },
        canActivate: [canActivateUser, projectsViewCanActivate],
        title: 'Projects'
    },
    {
        path: PROJECTS_ENG_BASE_URL,
        component: GtthProjectsEngComponent,
        data: {
            baseUrl: PROJECTS_ENG_BASE_URL,
            authorities: UserAuthorities.ALL_SIKLU,
            pageTitle
        },
        canActivate: [canActivateUser, projectEngViewCanActivate],
        children: [
            {
                path: '',
                component: GtthProjectsComponent
            }
        ],
        title: 'Projects'
    }
];

export const folderPopupRoute: Routes = [
    {
        path: 'folder/new/:parentId',
        component: GtthFolderPopupComponent,
        data: {
            authorities: [UserAuthorities.COMPANY_ADMIN, UserAuthorities.COMPANY_PLANNER],
            pageTitle
        },
        resolve: { rootFolderIds: rootFolderIdsResolver },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'folder/new/:customerId/:parentId',
        component: GtthFolderPopupComponent,
        data: {
            authorities: [UserAuthorities.SIKLU_ACCOUNTANT, UserAuthorities.SIKLU_CUSTOMER_SUPPORT, UserAuthorities.SIKLU_SALES],
            pageTitle
        },
        resolve: { rootFolderIds: rootFolderIdsResolver },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'folder/:id/edit',
        component: GtthFolderPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'folder/:id/delete',
        component: GtthProjectsFolderDeletePopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser],
        outlet: 'popup'
    }
];

export const BOMConfigPopupRoute: Routes = [
    {
        path: 'BOM/new/:parentId',
        component: GtthCreateBOMConfiguratorPopupComponent,
        data: {
            authorities: [UserAuthorities.COMPANY_ADMIN, UserAuthorities.COMPANY_PLANNER],
            pageTitle
        },
        resolve: { rootFolderIds: rootFolderIdsResolver },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'BOM/new/:customerId/:parentId',
        component: GtthCreateBOMConfiguratorPopupComponent,
        data: {
            authorities: [UserAuthorities.SIKLU_ACCOUNTANT, UserAuthorities.SIKLU_CUSTOMER_SUPPORT, UserAuthorities.SIKLU_SALES],
            pageTitle
        },
        resolve: { rootFolderIds: rootFolderIdsResolver },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'BOM/:id/edit',
        component: GtthCreateBOMConfiguratorPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'BOM/:id/delete',
        component: GtthProjectsDeletePopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser],
        outlet: 'popup'
    }
];

export const projectPopupRoute: Routes = [
    {
        path: 'projects/new/:parentId',
        component: ProjectPopupComponent,
        data: {
            authorities: [UserAuthorities.COMPANY_ADMIN, UserAuthorities.COMPANY_PLANNER],
            pageTitle
        },
        resolve: { rootFolderIds: rootFolderIdsResolver },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'projects/new/:customerId/:parentId',
        component: ProjectPopupComponent,
        data: {
            authorities: [UserAuthorities.SIKLU_ACCOUNTANT, UserAuthorities.SIKLU_CUSTOMER_SUPPORT, UserAuthorities.SIKLU_SALES],
            pageTitle
        },
        resolve: { rootFolderIds: rootFolderIdsResolver },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'projects/import/:customerId/:parentId',
        component: ProjectImportPopupComponent,
        data: {
            authorities: UserAuthorities.SIKLU_USERS,
            pageTitle
        },
        resolve: { rootFolderIds: rootFolderIdsResolver },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'projects/:id/edit',
        component: ProjectPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'projects/:id/delete',
        component: GtthProjectsDeletePopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'projects/:id/clone',
        component: ProjectGtthClonePopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'projects/:id/migrate',
        component: ProjectGtthClonePopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'projects/:id/move',
        component: ProjectGtthMovePopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser],
        outlet: 'popup'
    },
    {
        path: 'projects/:id/layers',
        component: GtthLayersSettingsPopupComponent,
        data: {
            authorities: UserAuthorities.PROJECT_EDIT,
            pageTitle
        },
        canActivate: [canActivateUser],
        outlet: 'popup'
    }
];
